// Phase 25 iter-5e — Card para configurar o rol VET. Permite editar o
// número de colexiado + recordatorio de subir a chave ECDSA dende mobile.

import { CheckCircle2, KeyRound, Stethoscope } from 'lucide-react';
import { useState } from 'react';

import { describeAxiosError } from '../../api/errors';
import { updateMyProfile } from '../../api/users';
import Spinner from '../ui/Spinner';
import { useAuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

export function VetSetupCard(): JSX.Element {
  const { user, refreshUser } = useAuthContext();
  const { addToast } = useToast();
  const [vetLicense, setVetLicense] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return <></>;

  // O AuthenticatedUser non leva `vet_license`; só o devolve /users/me. Para
  // non engadir outro fetch, asumimos que se public_key xa está subida o
  // VET completou o setup; en caso contrario amosamos o form.
  const hasPublicKey = !!user.public_key;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!vetLicense.trim()) {
      setError('Introduce o número de colexiado.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await updateMyProfile({ vet_license: vetLicense.trim() });
      await refreshUser();
      addToast('Número de colexiado gardado. Será validado polo equipo dentro de 24 h.', 'success');
      setVetLicense('');
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido gardar o colexiado.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="border border-border rounded-2xl p-6 bg-white">
      <div className="flex items-center gap-2 mb-4">
        <Stethoscope size={18} className="text-muted" />
        <h3 className="font-semibold text-dark text-base">Setup como veterinario/a</h3>
      </div>

      <p className="text-sm text-muted mb-4">
        Para asinar eventos sanitarios precisamos verificar o teu número de colexiado e que teñas a
        túa chave ECDSA xerada dende a app móbil.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
            Número de colexiado
          </span>
          <input
            type="text"
            value={vetLicense}
            onChange={(e) => setVetLicense(e.target.value)}
            className="airbnb-input"
            placeholder="Ex. 2 / 3401"
          />
        </label>
        {error && <p className="text-xs text-rauxa">{error}</p>}
        <button type="submit" disabled={saving} className="btn-primary !py-2 !px-4 text-sm">
          {saving ? <Spinner size={14} /> : 'Gardar colexiado'}
        </button>
      </form>

      <div
        className={`mt-5 p-3 rounded-xl flex items-center gap-3 ${
          hasPublicKey ? 'bg-rauxa/10' : 'bg-amber-50'
        }`}
      >
        {hasPublicKey ? (
          <CheckCircle2 size={18} className="text-rauxa shrink-0" />
        ) : (
          <KeyRound size={18} className="text-amber-700 shrink-0" />
        )}
        <div>
          <div className="text-sm font-semibold text-dark">
            Chave ECDSA P-256 {hasPublicKey ? 'rexistrada' : 'pendente'}
          </div>
          <p className="text-xs text-muted mt-0.5">
            {hasPublicKey
              ? 'Xa podes asinar eventos sanitarios.'
              : 'A clave xerarase automaticamente cando crees o teu primeiro evento sanitario asinado desde a app móbil.'}
          </p>
        </div>
      </div>
    </section>
  );
}
